/**
 * B1 (PR2b) — Configuración → "Tienda en línea": el tenant enciende/apaga los
 * pedidos en línea y comparte su liga pública (link + QR de <origin>/<slug>/tienda).
 *
 * GATE DE ACTIVACIÓN de privacidad: encender exige el aviso publicado. El backend lo
 * enforce (assertAvisoPublished → 409); aquí además se DESHABILITA el switch y se
 * explica a dónde ir, para no chocar contra el 409 a ciegas. Gemelo de
 * BookingChannelSection (mismo patrón link/QR/toggle). Solo verticales con POS; el
 * backend ya lo gatea por módulo (RequiresModule boutique/gastro/gym).
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';

interface StoreSettings {
  onlineOrdersEnabled: boolean;
  holdTtlMinutes: number;
  avisoPublished: boolean;
  /** B2 — publicados sin inventario ni receta: se venden SIN LÍMITE (aviso, no bloqueo). */
  unlimitedPublished?: { count: number; names: string[] };
}

const errMsg = (e: any, fallback: string): string => {
  const m = e?.body?.message;
  return Array.isArray(m) ? m.join(' ') : m || e?.message || fallback;
};

export function StoreAdminCard() {
  const { user } = useAuth() as any;
  const slug: string = user?.tenant?.slug ?? '';

  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const url = slug ? `${window.location.origin}/${slug}/tienda` : null;

  useEffect(() => {
    api<StoreSettings>('/store/settings').then(setSettings).catch(() => setSettings(null));
  }, []);

  useEffect(() => {
    if (!url) { setQr(null); return; }
    QRCode.toDataURL(url, { width: 480, margin: 1 }).then(setQr).catch(() => setQr(null));
  }, [url]);

  // Backend gateado por módulo: si 404/no-carga, la card no se muestra (feature dormida).
  if (!settings) return null;

  const s = settings;
  const canEnable = s.avisoPublished; // el gate; el switch de encender se bloquea sin aviso.

  const toggle = async () => {
    const next = !s.onlineOrdersEnabled;
    setBusy(true); setErr(null); setCopied(false);
    try {
      const updated = await api<StoreSettings>('/store/settings/online-orders', {
        method: 'PUT', body: { enabled: next },
      });
      setSettings(updated);
    } catch (e: any) {
      // 409 del gate de aviso u otro → mensaje accionable, sin cambiar el switch.
      setErr(errMsg(e, 'No se pudo actualizar la tienda en línea.'));
    } finally { setBusy(false); }
  };

  const copy = () => {
    if (!url) return;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    }).catch(() => {});
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Tienda en línea</h3>
        <p className="text-sm text-slate-500">
          Tus clientes compran de tu catálogo y recogen en sucursal, vía liga o QR. Apagada, la liga y el QR
          dejan de funcionar de inmediato.
        </p>
      </div>

      <div className="space-y-4 px-6 py-5 md:px-8">
        {/* B2 — AVISO (no bloqueo): publicados sin inventario ni receta = sin límite. */}
        {!!s.unlimitedPublished?.count && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            <strong>{s.unlimitedPublished.count}</strong> producto{s.unlimitedPublished.count === 1 ? '' : 's'} publicado{s.unlimitedPublished.count === 1 ? '' : 's'} sin
            inventario ni receta — <strong>se venden sin límite</strong> (tú resuelves la disponibilidad
            en la entrega): {s.unlimitedPublished.names.join(', ')}{s.unlimitedPublished.count > s.unlimitedPublished.names.length ? '…' : ''}.
          </div>
        )}
        {/* Gate de aviso: sin aviso publicado no se puede encender (candado de activación). */}
        {!canEnable && !s.onlineOrdersEnabled && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Para <strong>encender la tienda</strong> primero publica tu <strong>aviso de privacidad</strong> en{' '}
            <strong>Configuración › Usuarios, acceso y legal</strong>. Es requisito legal: recabas datos de tus
            clientes al recibir pedidos.
          </p>
        )}

        <label className="flex items-center justify-between gap-4">
          <span>
            <span className="block text-sm font-medium text-slate-800">Pedidos en línea activos</span>
            <span className="block text-[12px] text-slate-400">
              {s.onlineOrdersEnabled
                ? 'Tu tienda acepta pedidos con pago en línea.'
                : 'Encendida, tu catálogo público acepta pedidos y cobra en línea.'}
            </span>
          </span>
          <button
            disabled={busy || (!s.onlineOrdersEnabled && !canEnable)}
            onClick={toggle}
            aria-label="Pedidos en línea"
            title={!s.onlineOrdersEnabled && !canEnable ? 'Publica tu aviso de privacidad para encender' : undefined}
            className={`relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-40 ${
              s.onlineOrdersEnabled ? 'bg-emerald-500' : 'bg-slate-300'
            }`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
              s.onlineOrdersEnabled ? 'left-[22px]' : 'left-0.5'
            }`} />
          </button>
        </label>

        <p className="text-[12px] text-slate-400">
          Los productos que aparecen en la tienda son los que marcas <strong>«Publicar en tienda»</strong> en{' '}
          <strong>Productos</strong>, con existencia en la sucursal.
        </p>

        {err && <p className="text-sm text-rose-600">{err}</p>}

        {/* Liga pública + QR (gemelo de Reservas en línea). */}
        {url && (
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <p className="text-[12px] font-medium text-slate-500">Tu liga de tienda</p>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly value={url} onFocus={(e) => e.target.select()}
                className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-600" />
              <button onClick={copy}
                className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50">
                {copied ? 'Copiado ✓' : 'Copiar'}
              </button>
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50">
                Abrir
              </a>
            </div>
            {qr && (
              <div className="mt-3 flex items-center gap-4">
                <img src={qr} alt="QR de la tienda" className="h-28 w-28 rounded-lg border border-slate-200 bg-white p-1" />
                <p className="text-[12px] text-slate-500">
                  Imprime o comparte el QR: quien lo escanee abre tu tienda.
                  {!s.onlineOrdersEnabled && <span className="text-amber-700"> Actívala arriba para que reciba pedidos.</span>}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
